import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useUser } from "@/context/userContext";

type AccessTokenClaims = {
    role?: string;
    roles?: string[];
};

const normalizeRole = (value: unknown): string =>
    String(value ?? "").trim().toLowerCase();

const isAdminLikeRole = (value: unknown): boolean => {
    const role = normalizeRole(value);
    return role === "admin" || role === "super_admin" || role === "superadmin";
};

const hasAdminRole = (userRole: unknown, authToken: string | null): boolean => {
    if (isAdminLikeRole(userRole)) return true;
    if (!authToken) return false;

    try {
        const claims = jwtDecode<AccessTokenClaims>(authToken);
        const tokenRoles = Array.isArray(claims.roles) ? claims.roles : [];
        if (tokenRoles.some(isAdminLikeRole)) return true;
        return isAdminLikeRole(claims.role);
    } catch {
        return false;
    }
};

const AdminOnlyRoute = () => {
    const { user, authToken, loading } = useUser();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
                <span className="brand-pill qit-loading-pill qit-loading-pill-page" aria-label="Loading" />
            </div>
        );
    }

    if (!user) {
        const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
        return <Navigate to={`/admin?redirect=${redirect}`} replace />;
    }
    if (!hasAdminRole(user.role, authToken)) return <Navigate to="/admin?forbidden=1" replace />;

    return <Outlet />;
};

export default AdminOnlyRoute;
